/**
 * The launch chapter's translation between the pinned client and the §8 components.
 *
 * The same three labels ch00 uses, for the same reason — a reader should be able to tell
 * at a glance which numbers are the platform's and which are this repository's arithmetic:
 *
 *   TRANSFORM  a shape change that loses nothing
 *   DERIVE     a value computed from what the contract does carry, stated as such
 *   GAP        something a screen needs that the contract does not expose
 *
 * Two properties of the launch models drive most of what is here.
 *
 * **The money-ish measures arrive as strings.** `complete_run_share`, `expected_impact`
 * and `objective_value` are decimal strings, because the platform refuses to put a
 * planning number through a binary float. They are parsed once, here, and a string that
 * does not parse becomes `null` — never `0`. A zero on a complete-run share is a claim
 * that no door opened with a complete run, and that is the opposite of "we could not read
 * the number".
 *
 * **`control_group_key` is a hardcoded null.** See `controlComparison` below and
 * `docs/adr/0042`. It is the field gate D3's against-control comparison would be computed
 * from, and it is null because the M6 pilot/control cohort assignment is a business
 * decision nobody has taken yet. It is therefore not a missing value to be filled in
 * later by this repository under any derivation: there is no cohort, so there is no
 * comparison, and the screen says so.
 */

import type {
  AllocationVersionModel,
  DispositionReasonModel,
  ProposalModel,
  QueueDepthModel,
  SizeRunReportModel,
} from '@/api/generated';
import { compareSizeNames, sizeName, type SizeName } from '@/planning';

// ── decimal strings ──────────────────────────────────────────────────────────

/**
 * TRANSFORM — a decimal string into a number, or `null`.
 *
 * `Number.parseFloat('12abc')` is `12`, which is precisely the silent-corruption path a
 * planning number must not take, so the whole string is validated before it is trusted.
 * An unparseable value is an absence with a reason, and every caller here is required by
 * `Measure`/`NumericCell` to say what that absence means.
 */
export function decimal(value: string | null | undefined): number | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (!/^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

// ── reason codes ─────────────────────────────────────────────────────────────

/**
 * DERIVE — an engine reason code into the catalogue key a planner reads.
 *
 * `GET /api/v1/launch/reason-codes` publishes the vocabulary and its docstring says each
 * code "maps to a catalogue key" — but the mapping is this repository's, because the key
 * has to exist in this repository's three catalogues. A code this build does not know is
 * NOT rendered as `⟦launch.reason.…⟧` and not hidden either: it gets the `unclassified`
 * message, whose `{code}` placeholder carries the engine's own identifier as the
 * reference datum it is (G6 — a code is data, the sentence around it is copy).
 */
const REASON_KEY: Readonly<Record<string, string>> = {
  demand_rank: 'launch.reason.demand_rank',
  pres_min_floor: 'launch.reason.pres_min_floor',
  door_protected: 'launch.reason.door_protected',
  capacity_capped: 'launch.reason.capacity_capped',
  case_rounded: 'launch.reason.case_rounded',
  size_run_dropped: 'launch.reason.size_run_dropped',
  reserve_carveout: 'launch.reason.reserve_carveout',
  withheld_for_chase: 'launch.reason.withheld_for_chase',
};

export const UNCLASSIFIED_REASON_KEY = 'launch.reason.unclassified';

/** The key and its ICU arguments — one key, one complete message, no concatenation. */
export interface ReasonMessage {
  readonly key: string;
  readonly values: Readonly<Record<string, string>>;
}

export function reasonMessage(code: string): ReasonMessage {
  const known = REASON_KEY[code];
  if (known !== undefined) return { key: known, values: {} };
  return { key: UNCLASSIFIED_REASON_KEY, values: { code } };
}

/** One governed reason a planner may record on a launch disposition, with its words. */
export interface DispositionReason {
  readonly code: string;
  /** The platform's label in the caller's language; the code itself where it sent none. */
  readonly label: string;
}

/**
 * TRANSFORM — the governed proposal-disposition vocabulary, narrowed to one verb.
 *
 * `GET /api/v1/launch/dispositions/reason-codes` (bata-platform ADR 5001) publishes each code
 * with the dispositions it may carry; a code recorded against a verb it does not list is
 * refused (422 `planning.allocation.disposition_reason_not_governed`). So only the codes whose
 * `dispositions` include `disposition` are offered, in the platform's order. The label is the
 * platform's — governance owns the vocabulary and its words, not this repository.
 */
export function dispositionReasonsFor(
  vocabulary: readonly DispositionReasonModel[], disposition: string,
): readonly DispositionReason[] {
  return vocabulary
    .filter((reason) => reason.code.trim() !== '' && reason.dispositions.includes(disposition))
    .map((reason) => ({ code: reason.code, label: reason.label.trim() === '' ? reason.code : reason.label }));
}

// ── the bulk unit ────────────────────────────────────────────────────────────

/**
 * One group of proposals a single bulk action can be taken over.
 *
 * `GET /api/v1/launch/bulk-groups` answers with a map whose key is the reason code and
 * the door, joined by a pipe — `group_for_bulk` groups "by reason code first and door
 * second, because that is the order a planner reasons in". The ids are the platform's,
 * not a selection this screen made, which is what makes the action auditable: the set
 * acted on is the set the platform published under that key.
 */
export interface BulkGroup {
  readonly reasonCode: string;
  /** `null` where the key carries no door. Never guessed, and never shown as one. */
  readonly storeId: string | null;
  readonly proposalIds: readonly string[];
}

/** The separator `group_for_bulk` joins the two halves of its key with. */
const GROUP_KEY_SEPARATOR = '|';

/**
 * TRANSFORM — the published map into its groups, in key order.
 *
 * A group with no proposal ids is dropped rather than offered: a bulk action over an
 * empty set is not a decision, and a control that fires one would record an action that
 * disposed of nothing.
 */
export function toBulkGroups(
  groups: Readonly<Record<string, readonly string[]>>,
): readonly BulkGroup[] {
  return Object.keys(groups)
    .sort((a, b) => a.localeCompare(b))
    .flatMap((key) => {
      const proposalIds = groups[key] ?? [];
      if (proposalIds.length === 0) return [];
      const separator = key.indexOf(GROUP_KEY_SEPARATOR);
      const reasonCode = separator === -1 ? key : key.slice(0, separator);
      const storeId = separator === -1 ? null : key.slice(separator + 1);
      return [{
        reasonCode,
        storeId: storeId === null || storeId.trim() === '' ? null : storeId,
        proposalIds,
      }];
    });
}

/** The key a group is identified by in the picker — the platform's own, untouched. */
export function bulkGroupKey(group: BulkGroup): string {
  return group.storeId === null
    ? group.reasonCode
    : `${group.reasonCode}${GROUP_KEY_SEPARATOR}${group.storeId}`;
}

// ── allocation versions ──────────────────────────────────────────────────────

/**
 * DERIVE — the version a screen reads.
 *
 * The endpoint's docstring says "every engine run for an option, **oldest first**", so
 * the newest run is the last item. It is derived from the documented order rather than
 * from a timestamp because `AllocationVersionModel` publishes none; if the order ever
 * stops being guaranteed, this is the one place that has to change.
 */
export function latestVersion(
  versions: readonly AllocationVersionModel[],
): AllocationVersionModel | undefined {
  return versions.length === 0 ? undefined : versions[versions.length - 1];
}

/**
 * DERIVE — reqs/03 §3.5's residual: reserved + allocated + held = `Q_launch`, exactly.
 *
 * It is computed and shown rather than asserted, for the reason the bridge is shown:
 * a residual that does not close is the first sign the carve-out arithmetic is wrong,
 * and a screen that hid it would let a planner approve a shipment list on the assumption
 * that it did close.
 */
export function reconciliationResidual(version: AllocationVersionModel): number {
  return version.launch_units
    - version.reserved_units
    - version.allocated_units
    - version.withheld_units;
}

// ── the size-run integrity report ────────────────────────────────────────────

/**
 * TRANSFORM — the report row for one version out of the returned set.
 *
 * The endpoint answers with a list; a screen scoped to a version wants its row and must
 * not silently render another version's. Where the version is not in the list the answer
 * is `undefined` and the screen renders its empty state, rather than falling back to the
 * first row — which would show gate D3's measurement for a run the planner is not
 * looking at.
 */
export function reportFor(
  rows: readonly SizeRunReportModel[],
  allocationVersionId: string,
): SizeRunReportModel | undefined {
  return rows.find((row) => row.allocation_version_id === allocationVersionId);
}

/**
 * The against-control comparison gate D3 reads — and why there is not one.
 *
 * reqs/08 §8 states the gate as *"size-run integrity and availability improvement
 * verified **against control**"*, and names it the programme's core value hypothesis.
 * The comparison therefore needs two cohorts: the doors running the new allocation and a
 * control group that is not. `SizeRunReportModel.control_group_key` is the field that
 * would name the second one, and the platform persists it as a hardcoded null pending
 * the M6 pilot/control cohort assignment — a business decision that has not been taken.
 *
 * So the comparison **cannot be computed**, and this function returns that fact rather
 * than a number. Three renderings were available and two of them are wrong:
 *
 *   * `0` would state that the treatment and the control performed identically, which is
 *     a finding — and the strongest possible negative finding about the hypothesis the
 *     whole programme rests on. It would be a fabricated one;
 *   * a bare dash reads as "no difference" in a row of differences, which is the same
 *     claim with deniability;
 *   * an absence with a reason says the cohort has not been assigned, which is true, is
 *     actionable by the person who can assign it, and cannot be mistaken for evidence.
 *
 * The third is what `Measure`'s `absent.reasonKey` exists for (`docs/adr/0012`, `0014`),
 * and `docs/adr/0042` records the decision.
 */
export interface ControlComparison {
  /** Always `false` in this spec version. Kept as a field so a caller must branch. */
  readonly available: boolean;
  /** The cohort key, when one is ever assigned. */
  readonly controlGroupKey: string | null;
  /** ICU key naming why there is no comparison. Rendered, never swallowed. */
  readonly reasonKey: string;
}

export const CONTROL_UNASSIGNED_KEY = 'launch.sizeRun.controlUnassigned';

export function controlComparison(report: SizeRunReportModel): ControlComparison {
  const key = report.control_group_key ?? null;
  return {
    available: typeof key === 'string' && key.trim() !== '',
    controlGroupKey: key,
    reasonKey: CONTROL_UNASSIGNED_KEY,
  };
}

// ── rows ─────────────────────────────────────────────────────────────────────

/** One shipment line, with its decimal string already parsed. */
export interface ShipmentLine {
  readonly proposalId: string;
  readonly storeId: string;
  /** The opaque key: an identifier for joins and the export's id column, never shown. */
  readonly sizeKey: string;
  /** The size as the customer names it, or absent with the platform's reason (ADR 9027). */
  readonly size: SizeName;
  readonly units: number;
  readonly shipWeek: string;
  readonly reasonCode: string;
  /** `null` where the engine's decimal did not parse — never `0`. */
  readonly expectedImpact: number | null;
  readonly currencyCode: string;
  /**
   * The line's first recorded decision and who took it (bata-platform ADR 5001, ADR 9021's
   * names): `null` while the line is undecided — never a default decision. Optional so a line
   * built by hand need not carry them.
   */
  readonly disposition?: string | null;
  readonly dispositionActor?: string | null;
  readonly dispositionActorName?: string | null;
  /** Why the actor has no name (ADR 2127): `machine`, `service_account` or `no_profile`; `null` where a name stands. */
  readonly dispositionActorNameAbsentReason?: string | null;
}

/** The decision words a launch line can carry; anything else is shown as its code. */
export const LAUNCH_DISPOSITION_KEY: Readonly<Record<string, string>> = {
  pending: 'launch.disposition.pending',
  approve: 'launch.disposition.approve',
  adjust: 'launch.disposition.adjust',
  reject: 'launch.disposition.reject',
  defer: 'launch.disposition.defer',
};

/**
 * TRANSFORM + GAP — a proposal into a row.
 *
 * GAP: `ProposalModel` carries `store_id` and no door name. The store code is the
 * business key and it is what the column shows; a display name would be reference data
 * the contract does not publish, and inventing one would put an unsourced label beside a
 * sourced number.
 */
export function toShipmentLines(rows: readonly ProposalModel[]): readonly ShipmentLine[] {
  return rows.map((row) => ({
    proposalId: row.proposal_id,
    storeId: row.store_id,
    sizeKey: row.size_key,
    size: sizeName(row),
    units: row.units,
    shipWeek: row.ship_week,
    reasonCode: row.reason_code,
    expectedImpact: decimal(row.expected_impact),
    currencyCode: row.currency_code,
    disposition: row.disposition ?? null,
    dispositionActor: row.disposition_actor ?? null,
    dispositionActorName: row.disposition_actor_name ?? null,
    dispositionActorNameAbsentReason: row.disposition_actor_name_absent_reason ?? null,
  }));
}

/**
 * The shipment list in the order the export's perimeter declares: by door, then by the size's
 * place in the run — `size_run_position`, never the key's spelling, which is a hash (ADR 9027).
 * An unnamed size sorts after the door's named ones. Equal lines keep the read's order.
 */
export function inShipmentOrder(lines: readonly ShipmentLine[]): readonly ShipmentLine[] {
  return lines
    .map((line, index) => ({ line, index }))
    .sort((a, b) =>
      (a.line.storeId < b.line.storeId ? -1 : a.line.storeId > b.line.storeId ? 1 : 0)
      || compareSizeNames(a.line.size, b.line.size)
      || a.index - b.index)
    .map((entry) => entry.line);
}

/** One reason-code group, with its expected impact parsed. */
export interface QueueGroup {
  readonly reasonCode: string;
  readonly lineCount: number;
  readonly units: number;
  readonly expectedImpact: number | null;
}

export function toQueueGroups(rows: readonly QueueDepthModel[]): readonly QueueGroup[] {
  return rows.map((row) => ({
    reasonCode: row.reason_code,
    lineCount: row.line_count,
    units: row.units,
    expectedImpact: decimal(row.expected_impact),
  }));
}
